import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Clock } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { TOWN_PAGES } from "@/data/townPages";

const GROUPS = [
  {
    name: "Chester County",
    towns: ["West Chester", "Exton", "Downingtown", "Malvern", "Paoli", "Chester Springs", "Phoenixville", "Thorndale", "Coatesville"],
  },
  {
    name: "Route 1 Corridor & Southern Chester County",
    towns: ["Kennett Square", "Chadds Ford", "West Grove", "Concordville", "Glen Mills"],
  },
  {
    name: "Delaware County & the Main Line",
    towns: ["Media", "Havertown", "Newtown Square", "Wayne", "King of Prussia"],
  },
];

export const HOW_IT_WORKS = [
  ["Regular, on-time stocking", "We restock on a dependable schedule, adjusted to what your location actually goes through — drinks, snacks, chips, candy, and healthier options."],
  ["Cleaning on every visit", "Every service visit includes a full wipe-down of the machine and a freshness check of everything inside."],
  ["Maintenance handled by us", "If the cooler, screen, or card reader needs attention, we take care of it — you never troubleshoot equipment."],
  ["Custom product selection", "Your location's mix is built around what your people ask for. Anyone can request items by scanning the QR code on the machine."],
  ["Prompt response to issues", "Reach us by phone, email, or the machine's QR code. A real person follows up fast during service hours."],
];

const townLink = (name) => {
  const page = TOWN_PAGES.find((p) => p.name === name);
  return page ? `/areas/${page.slug}` : null;
};

const ServiceArea = () => {
  const navigate = useNavigate();
  return (
    <div data-testid="service-area-page">
      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-48 lg:pb-28">
        <div aria-hidden="true" className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-vortex-blue/15 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.1}>
            <Overline>Coverage — Western Philadelphia Suburbs</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-heading font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="service-area-heading">
            <MaskedLine delay={0.25}>SMART COOLER VENDING SERVICE AREA:</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-vortex-cyan">WEST CHESTER, PA</span> &
            </MaskedLine>
            <MaskedLine delay={0.55}>SURROUNDING TOWNS</MaskedLine>
          </h1>
          <Reveal delay={0.65}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Vortex Vending Services LLC is based in West Chester and services
              businesses across the surrounding area — roughly a 20-mile radius
              covering Chester County, the Route 1 corridor, Delaware County, and
              the Main Line.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5" data-testid="towns-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>Towns Currently Served</Overline>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-12 md:grid-cols-3">
            {GROUPS.map((g, gi) => (
              <Reveal key={g.name} delay={0.08 * gi}>
                <div>
                  <h2 className="font-heading text-xl font-bold tracking-tight text-white">{g.name}</h2>
                  <ul className="mt-5 space-y-3">
                    {g.towns.map((t) => (
                      <li key={t} className="flex items-center gap-3 text-sm text-slate-300" data-testid={`town-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                        <MapPin size={13} className="shrink-0 text-vortex-cyan" />
                        {townLink(t) ? (
                          <Link to={townLink(t)} className="underline decoration-white/20 underline-offset-4 transition-colors hover:text-vortex-cyan">{t}</Link>
                        ) : t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-xl text-sm text-slate-500">
              Just outside these towns? Reach out — the service area grows as new
              locations come online.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-vortex-surface/20" data-testid="how-service-works">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>How Service Works</Overline>
            <h2 className="mt-6 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">
              One machine, fully looked after.
            </h2>
          </Reveal>
          <div className="mt-12">
            {HOW_IT_WORKS.map(([title, body], i) => (
              <Reveal key={title} delay={0.06 * i}>
                <div className="group flex flex-col gap-3 border-t border-white/10 py-8 transition-colors duration-500 hover:border-vortex-cyan/30 sm:flex-row sm:items-start sm:gap-10" data-testid={`service-row-${i}`}>
                  <span className="font-mono text-sm tracking-[0.25em] text-vortex-cyan">{`0${i + 1}`}</span>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold tracking-tight text-white transition-transform duration-500 group-hover:translate-x-2 sm:text-2xl">{title}</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-white/10" />
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 flex items-center gap-5 border border-white/10 bg-vortex-ink/60 px-8 py-6" data-testid="service-hours">
              <Clock size={20} className="shrink-0 text-vortex-cyan" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Service Hours</p>
                <p className="mt-1 font-heading text-xl font-bold text-white">Monday–Friday, 8:00 AM – 8:00 PM</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <Reveal>
          <h2 className="max-w-3xl font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
            IN THE AREA? <span className="text-vortex-cyan">LET'S STOCK YOUR SPACE.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="service-area-call-link"
              href="tel:+16109838060"
              className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_50px_rgba(92,179,226,0.5)]"
            >
              <Phone size={16} />
              Call (610) 983-8060
            </a>
            <button
              data-testid="service-area-contact-button"
              onClick={() => navigate("/contact")}
              className="group flex items-center gap-3 rounded-full border border-white/15 px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-slate-300 transition-all duration-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan"
            >
              Send a message
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default ServiceArea;
